import Utils from '../../../../utils/underscore-es6'
import type { GroupedItem } from '../models'
import type { ProductDataVariant } from '../../../../services/product/product-data'

type Item = ProductDataVariant

export enum ProductProperties {
  baseproduct = 'baseproduct',
  colorflavour = 'colorflavour',
  brand = 'brand',
  packandcarton = 'packandcarton'
}

export enum GroupByFamilies {
  accessory = 'accessories',
  consumable = 'consumables',
  device = 'devices'
}

type GroupByDataType = {
  productGroups?: Array<{
    productFamily?: string
    productProperty?: string
  }>
  except?: string[]
}

const GroupByProperties = {
  [ProductProperties.baseproduct]: 'baseArticle.baseArticleCode',
  [ProductProperties.colorflavour]: 'nuance.code',
  [ProductProperties.brand]: 'brand'
}

const isNumber = (value?: number) => typeof value === 'number'

/**
 * Computes a deterministic canonical code for a pack/carton cluster.
 *
 * The product API is symmetric: every article in a cluster lists all of its
 * counterpart codes in `relatedPackCartonCodes` (excluding its own code), but
 * the order of those codes may differ between articles.
 */
const getPackCartonCanonicalCode = (item: Item): string | undefined => {
  const { globalArticleCode, relatedPackCartonCodes } = item
  if (!relatedPackCartonCodes?.length) {
    return ''
  }
  const closure = [globalArticleCode, ...relatedPackCartonCodes].filter(
    (code): code is string => Boolean(code)
  )
  const uniqueSorted = Array.from(new Set(closure)).sort((a, b) =>
    a.localeCompare(b)
  )
  return uniqueSorted.join('-')
}

const calcGroupingKey = (
  item: Item,
  data?: GroupByDataType,
  index?: number
) => {
  const { productGroups = [], except = [] } = { ...data }
  if (item?.globalArticleCode && except.includes(item.globalArticleCode)) {
    return {
      groupingKey: String(index)
    }
  }
  const family = Utils.get(item, 'productFamily')
  const familyLowerCase = family?.toLowerCase()
  const group = productGroups.find(
    ({ productFamily }) =>
      productFamily ===
      (GroupByFamilies[familyLowerCase as keyof typeof GroupByFamilies] ??
        familyLowerCase)
  )

  if (!group) {
    return {
      groupingKey: String(index)
    }
  }

  const { productFamily, productProperty } = group

  if (productProperty === ProductProperties.packandcarton) {
    const canonicalCode = getPackCartonCanonicalCode(item)
    if (canonicalCode?.length) {
      return {
        groupingKey: `${productFamily}-${canonicalCode}`,
        productProperty
      }
    }
    return { groupingKey: String(index) }
  }

  const property =
    GroupByProperties[productProperty as keyof typeof GroupByProperties]

  const key = property ? Utils.get(item, property, property) : index

  return {
    groupingKey: `${productFamily}-${String(key)}`,
    productProperty
  }
}

export type MapperParams = {
  item: Item
  acc: Record<string, GroupedItem>
  key: string
  nuanceUnivocalIndex?: number
}

export type MapperFunction = (params: MapperParams) => GroupedItem

export const productMapper = ({
  item,
  acc,
  key,
  nuanceUnivocalIndex
}: {
  item: Item
  acc: Record<string, GroupedItem>
  key: string
  nuanceUnivocalIndex?: number
}): GroupedItem => {
  const {
    baseArticle,
    baseArticleCode,
    globalArticleCode: code,
    badges = [],
    nuance = {},
    seo = {},
    tags = [],
    assets = [],
    unitOfMeasure
  } = item
  const prevVariant = [
    ...((acc[key] as GroupedItem | undefined)?.variants || [])
  ]
  const nuanceObj = nuance || {}
  const { code: nuanceCode } = (nuanceObj as { code?: string }) || { code: '' }

  return {
    code: baseArticle?.baseArticleCode || baseArticleCode || '',
    variants: [
      ...prevVariant,
      {
        ...item,
        code: code || '',
        badges,
        nuance: {
          ...nuance,
          code: isNumber(nuanceUnivocalIndex)
            ? `${nuanceCode || ''}${nuanceUnivocalIndex}`
            : nuanceCode || ''
        } as ProductDataVariant['nuance'],
        seo,
        tags,
        assets,
        unitOfMeasure
      } as ProductDataVariant
    ]
  }
}

export const groupBy = (
  items: Item[],
  mapper?: MapperFunction,
  data?: GroupByDataType
): { [key: string]: GroupedItem } => {
  const mapperFunction = mapper || productMapper
  return items.reduce(
    (acc, item, index) => {
      const { groupingKey, productProperty } = calcGroupingKey(
        item,
        data,
        index
      )

      // Forced case in baseproduct. If nuance color is different for rings in the future, remove it
      const nuanceUnivocalIndex =
        productProperty === ProductProperties.baseproduct ? index : undefined

      return {
        ...acc,
        [groupingKey]: {
          // ...item,
          ...mapperFunction({
            item,
            acc,
            key: groupingKey,
            nuanceUnivocalIndex
          })
        }
      }
    },
    {} as Record<string, GroupedItem>
  )
}
